/**
 * Organizations (/dashboard/admin/orgs, /dashboard/admin/orgs/<orgId>): Chicago Politics staff turn features on or off
 * and set limits per team (Permissions Phase 2; worker/src/dashboard-staff-orgs.js). Not designed: built from the
 * Approvals / Access parts (page head, members table, chips, switches, buttons, toasts). Every staff member reads;
 * staff owners/admins change a feature (a required reason, an optional expiry) or reset it to the group default. The
 * Worker refuses private-data features for advocacy teams (422), and the switch is disabled for them here.
 * Paints from snapshot:admin-orgs / snapshot:admin-org. The Plan section assigns a plan and its add-ons
 * (auth/plans.ts, accounts 0023; PUT admin/orgs/<orgId>/plan): the features' defaults follow it.
 */
import { Dropdown } from "../components/shared/Dropdown";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { initialsOf, lastActiveLabel } from "../account/logic";
import { Switch } from "../components/shared/ui";
import { apiAuditRow, dateInput, expiryIso, overrideText, TEAM_GROUP_LABEL, valueText } from "./logic";
import type { AdminFeature, AdminOrgSnapshot, AdminOrgsSnapshot, AdminPlanChoice, ApiAuditEntry, AuditEntry, Override, PlanSetting } from "./types";
import { PLANS, ADDONS, type AddonKey, type PlanKey } from "../auth/plans";
import { Crumbs, sectionCrumb } from "../app/crumbs";

async function call<T>(path: string, method: string, body?: unknown): Promise<T | null> {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
  const data = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) toastStore.getState().show({ message: errorText(data) ?? "Couldn't save that. Try again." });
  return res?.ok ? (data as T) : null;
}

const fullDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : undefined);
const orgPath = (id: string) => `/dashboard/admin/orgs/${encodeURIComponent(id)}`;

/** A column header in the same part as the rows' column (RowCell with a width, else RowMain), so they line up. */
export const Head = ({ children, width, min }: { children?: string; width?: number; min?: number }) =>
  width != null ? <RowCell width={width}>{children && <span role="columnheader">{children}</span>}</RowCell>
    : <RowMain min={min}>{children && <span role="columnheader">{children}</span>}</RowMain>;

/** A table cell: a fixed column with a width, else a share of the rest (RowMain). */
export const Cell = ({ children, width, min, className, title, align }: { children?: ReactNode; width?: number; min?: number; className?: string; title?: string; align?: "end" }) =>
  width != null ? <RowCell width={width} align={align}><span role="cell" className={className} title={title}>{children}</span></RowCell>
    : <RowMain min={min}><span role="cell" className={className} title={title}>{children}</span></RowMain>;

const table = (attrs: Record<string, string>) => ({ role: "table", ...attrs });

/**
 * The credential audit (GET /api/dashboard/admin/api-audit): API keys made, edited, revoked; apps connected and
 * disconnected. One org's on its page, every org's on the list (with the Organization column). Never a hash.
 */
function ApiAudit({ orgId, initial, next: first, withOrg }: { orgId?: string; initial: ApiAuditEntry[]; next: number | null; withOrg?: boolean }) {
  const [entries, setEntries] = useState(initial);
  const [next, setNext] = useState(first);
  const now = new Date();
  const more = async () => {
    const page = await call<{ entries: ApiAuditEntry[]; next: number | null }>(`/api/dashboard/admin/api-audit?${orgId ? `org=${encodeURIComponent(orgId)}&` : ""}before=${next}`, "GET");
    if (page) { setEntries((a) => [...a, ...page.entries.filter((e) => !a.some((x) => x.id === e.id))]); setNext(page.next); }
  };
  return (
    <section className="approvals-recent" aria-labelledby="admin-api-audit">
      <h2 className="section-title" id="admin-api-audit">API audit</h2>
      {entries.length === 0 ? <p className="empty-panel">No changes yet.</p> : (
        <List as="div" className={`members admin-api-audit${withOrg ? " admin-api-audit--orgs" : ""}`} {...table({ "aria-labelledby": "admin-api-audit" })}>
          <ListRow as="div" className="members__head ls-head" role="row">
            {withOrg && <Head min={90}>Organization</Head>}
            <Head min={110}>Name</Head><Head width={110}>Action</Head><Head min={140}>Change</Head>
            <Head min={100}>Reason</Head><Head min={80}>By</Head><Head width={96}>When</Head>
          </ListRow>
          {entries.map((e) => {
            const r = apiAuditRow(e);
            return (
              <ListRow key={e.id} as="div" className="members__row" role="row">
                {withOrg && <Cell min={90} className="members__active">{e.orgId ? <a className="link" href={orgPath(e.orgId)}>{e.orgName ?? e.orgId}</a> : "Personal"}</Cell>}
                <Cell min={110} className="approvals__org"><RowTitle as="b">{r.name}</RowTitle><RowSub>{[r.detail, r.owner].filter(Boolean).join(" · ")}</RowSub></Cell>
                <Cell width={110}><span className={`chip ${e.action.endsWith("revoke") ? "chip--negative" : "chip--neutral"}`}>{r.action}</span></Cell>
                <Cell min={140} className="members__active admin-audit__reason">{r.change}</Cell>
                <Cell min={100} className="members__active admin-audit__reason">{e.reason ?? ""}</Cell>
                <Cell min={80} className="members__active">{e.by ?? ""}</Cell>
                <Cell width={96} className="members__active" title={fullDate(e.at)}>{lastActiveLabel(e.at, now)}</Cell>
              </ListRow>
            );
          })}
        </List>
      )}
      {next != null && <button className="btn btn--secondary btn--small admin-more" onClick={() => void more()}>Load more</button>}
    </section>
  );
}

// ---------- the list

export function AdminOrgs({ initial }: { initial: AdminOrgsSnapshot }) {
  useEffect(() => { document.title = "Organizations · Chicago Politics"; }, []);
  return (
    <div className="access-page admin-page">
      <header className="access-head"><h1 className="page-title">Organizations</h1></header>
      {initial.orgs.length === 0 ? <p className="empty-panel">No organizations yet.</p> : (
        <List as="div" className="members admin-orgs" {...table({ "aria-label": "Organizations" })}>
          <ListRow as="div" className="members__head ls-head" role="row">
            <Head min={160}>Organization</Head><Head width={120}>Members</Head><Head width={120}>Overrides</Head>
          </ListRow>
          {initial.orgs.map((o) => (
            <ListRow key={o.id} as="div" className="members__row admin-orgs__row" role="row" href={orgPath(o.id)}>
              <Cell min={160} className="approvals__org">
                <RowTitle as="b">{o.name}</RowTitle>
                <RowSub>{TEAM_GROUP_LABEL[o.group]}{o.ward ? ` · Ward ${o.ward}` : ""}{o.plan ? ` · ${PLANS[o.plan].name}` : ""}</RowSub>
              </Cell>
              <Cell width={120} className="members__active">{o.members}</Cell>
              <Cell width={120}>{o.overrides > 0 ? <span className="chip chip--warning">{o.overrides}</span> : <span className="members__active">0</span>}</Cell>
            </ListRow>
          ))}
        </List>
      )}
      {initial.apiAudit && <ApiAudit initial={initial.apiAudit} next={initial.apiAuditNext ?? null} withOrg />}
    </div>
  );
}

// ---------- one organization

export function AdminOrg({ initial }: { initial: AdminOrgSnapshot }) {
  const { org, canEdit } = initial;
  const [features, setFeatures] = useState(initial.features);
  const [audit, setAudit] = useState(initial.audit);
  const [next, setNext] = useState(initial.auditNext);
  const now = new Date();
  useEffect(() => { document.title = `${org.name} · Chicago Politics`; }, [org.name]);

  const updated = (f: AdminFeature, entry: AuditEntry | null) => {
    setFeatures((fs) => fs.map((x) => (x.key === f.key ? f : x)));
    if (entry) setAudit((a) => [entry, ...a.filter((e) => e.id !== entry.id)]);
  };
  const more = async () => {
    const page = await call<{ entries: AuditEntry[]; next: number | null }>(`/api/dashboard/admin/audit?org=${encodeURIComponent(org.id)}&before=${next}`, "GET");
    if (page) { setAudit((a) => [...a, ...page.entries]); setNext(page.next); }
  };
  const label = (key: string | null) => features.find((f) => f.key === key);

  return (
    <div className="access-page admin-page">
      <Crumbs trail={[sectionCrumb("approvals"), { label: "Organizations", href: "/dashboard/admin/orgs" }, { label: org.name }]} />
      <header className="access-head">
        <div>
          <h1 className="page-title">{org.name}</h1>
          <p className="access-sub">{TEAM_GROUP_LABEL[org.group]}{org.ward ? ` · Ward ${org.ward}` : ""}</p>
        </div>
      </header>

      {initial.plans && <PlanPanel orgId={org.id} canEdit={canEdit} plans={initial.plans} initial={initial.plan ?? null}
        onSaved={(fs, entry) => { setFeatures(fs); if (entry) setAudit((a) => [entry, ...a.filter((e) => e.id !== entry.id)]); }} />}

      <section className="approvals-recent" aria-labelledby="admin-features">
        <h2 className="section-title" id="admin-features">Features</h2>
        <List as="div" className="members admin-features" {...table({ "aria-labelledby": "admin-features" })}>
          <ListRow as="div" className="members__head ls-head" role="row">
            <Head min={160}>Feature</Head><Head width={72}>Default</Head><Head width={110}>Setting</Head>
            <Head width={150}>Expires</Head><Head min={120}>Reason</Head><Head width={96} />
          </ListRow>
          {features.map((f) => <FeatureRow key={f.key} orgId={org.id} f={f} canEdit={canEdit} onSaved={updated} />)}
        </List>
      </section>

      <section className="approvals-recent" aria-labelledby="admin-members">
        <h2 className="section-title" id="admin-members">Members</h2>
        <List as="div" className="members admin-members" {...table({ "aria-labelledby": "admin-members" })}>
          {initial.members.map((m) => (
            <ListRow key={m.id} as="div" className="members__row" role="row">
              <Cell min={160} className="members__who">
                <span className="members__avatar" aria-hidden>{initialsOf(m.name || m.email.split("@")[0]!.replace(/[._-]+/g, " "))}</span>
                <span><RowTitle as="b">{m.name || m.email}</RowTitle><RowSub>{m.email}</RowSub></span>
              </Cell>
              <Cell width={120} className="members__active">{m.role.split(",").map((r) => r.trim()[0]!.toUpperCase() + r.trim().slice(1)).join(", ")}</Cell>
              <Cell width={120} className="members__active" title={fullDate(m.joinedAt)}>{lastActiveLabel(m.joinedAt, now)}</Cell>
            </ListRow>
          ))}
        </List>
      </section>

      {initial.apiKeys.length > 0 && (
        <section className="approvals-recent" aria-labelledby="admin-keys">
          <h2 className="section-title" id="admin-keys">API keys</h2>
          <List as="div" className="members admin-keys" {...table({ "aria-labelledby": "admin-keys" })}>
            <ListRow as="div" className="members__head ls-head" role="row">
              <Head min={120}>Key</Head><Head min={120}>Owner</Head><Head min={160}>Scopes</Head><Head width={110}>Last used</Head>
            </ListRow>
            {initial.apiKeys.map((k) => (
              <ListRow key={k.id} as="div" className="members__row" role="row">
                <Cell min={120} className="approvals__org"><RowTitle as="b">{k.name || k.prefix}</RowTitle><RowSub><code>{k.prefix}…</code>{k.revokedAt ? " · Revoked" : ""}</RowSub></Cell>
                <Cell min={120} className="members__active">{k.owner ?? ""}</Cell>
                <Cell min={160} className="admin-keys__scopes">{k.scopes.map((s) => <span key={s} className="chip chip--neutral">{s}</span>)}</Cell>
                <Cell width={110} className="members__active" title={fullDate(k.lastUsedAt)}>{k.lastUsedAt ? lastActiveLabel(k.lastUsedAt, now) : "Never"}</Cell>
              </ListRow>
            ))}
          </List>
        </section>
      )}

      {initial.usage.length > 0 && (
        <section className="approvals-recent" aria-labelledby="admin-usage">
          <h2 className="section-title" id="admin-usage">API usage</h2>
          <List as="div" className="members admin-usage" {...table({ "aria-labelledby": "admin-usage" })}>
            <ListRow as="div" className="members__head ls-head" role="row"><Head width={140}>Day</Head><Head width={140}>Requests</Head><Head width={140}>AI answers</Head></ListRow>
            {initial.usage.map((u) => (
              <ListRow key={u.day} as="div" className="members__row" role="row">
                <Cell width={140} className="members__active">{u.day}</Cell>
                <Cell width={140} className="members__active">{u.requests.toLocaleString("en-US")}</Cell>
                <Cell width={140} className="members__active">{u.aiCalls.toLocaleString("en-US")}</Cell>
              </ListRow>
            ))}
          </List>
        </section>
      )}

      <section className="approvals-recent" aria-labelledby="admin-audit">
        <h2 className="section-title" id="admin-audit">Audit</h2>
        {audit.length === 0 ? <p className="empty-panel">No changes yet.</p> : (
          <List as="div" className="members admin-audit" {...table({ "aria-labelledby": "admin-audit" })}>
            <ListRow as="div" className="members__head ls-head" role="row">
              <Head min={100}>Feature</Head><Head min={120}>Change</Head><Head min={120}>Reason</Head>
              <Head min={80}>By</Head><Head width={96}>When</Head>
            </ListRow>
            {audit.map((e) => {
              const f = label(e.feature), kind = f?.kind ?? "flag", plan = e.action === "plan.set";
              return (
                <ListRow key={e.id} as="div" className="members__row" role="row">
                  <Cell min={100} className="approvals__org"><RowTitle as="b">{plan ? "Plan" : f?.description ?? e.feature}</RowTitle></Cell>
                  <Cell min={120} className="members__active">{plan
                    ? `${planText(e.before as PlanSetting | null)} → ${planText(e.after as PlanSetting | null)}`
                    : `${overrideText(kind, e.before as Override | null)} → ${overrideText(kind, e.after as Override | null)}`}</Cell>
                  <Cell min={120} className="members__active admin-audit__reason">{e.reason ?? ""}</Cell>
                  <Cell min={80} className="members__active">{e.by ?? ""}</Cell>
                  <Cell width={96} className="members__active" title={fullDate(e.at)}>{lastActiveLabel(e.at, now)}</Cell>
                </ListRow>
              );
            })}
          </List>
        )}
        {next != null && <button className="btn btn--secondary btn--small admin-more" onClick={() => void more()}>Load more</button>}
      </section>

      {initial.apiAudit && <ApiAudit orgId={org.id} initial={initial.apiAudit} next={initial.apiAuditNext ?? null} />}
    </div>
  );
}

/** One catalog feature: default, a switch (flag) or number (limit), an expiry date, a required reason; Save / Reset. */
function FeatureRow({ orgId, f, canEdit, onSaved }: { orgId: string; f: AdminFeature; canEdit: boolean; onSaved: (f: AdminFeature, e: AuditEntry | null) => void }) {
  const live = f.override && !f.override.expired ? f.override : null;
  const start = () => ({ on: f.effective === true, limit: f.kind === "limit" && typeof f.effective === "number" ? String(f.effective) : "", expires: dateInput(live?.expiresAt), reason: "" });
  const [draft, setDraft] = useState(start);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(start()), [f]);

  const value = f.kind === "flag" ? draft.on : draft.limit.trim() === "" ? null : Number(draft.limit);
  const changed = (f.kind === "flag" ? draft.on !== (f.effective === true) : value !== f.effective) || draft.expires !== dateInput(live?.expiresAt);
  const validLimit = f.kind === "flag" || value === null || (Number.isInteger(value) && (value as number) >= 0);
  const blocked = f.kind === "flag" && !f.grantable && !draft.on && !f.effective;   // the privacy rule: never on for this group
  const path = `/api/dashboard/admin/orgs/${encodeURIComponent(orgId)}/features/${encodeURIComponent(f.key)}`;

  const save = async () => {
    if (!draft.reason.trim()) { toastStore.getState().show({ message: "A reason is required." }); return; }
    setBusy(true);
    const body = { ...(f.kind === "flag" ? { enabled: draft.on } : value === null ? { enabled: false } : { limit_value: value }), expires_at: expiryIso(draft.expires), reason: draft.reason.trim() };
    const res = await call<{ feature: AdminFeature; audit: AuditEntry | null }>(path, "PUT", body);
    setBusy(false);
    if (res) { onSaved(res.feature, res.audit); toastStore.getState().show({ message: `Saved ${f.description}` }); }
  };
  const reset = async () => {
    setBusy(true);
    const res = await call<{ feature: AdminFeature; audit: AuditEntry | null }>(path, "DELETE", draft.reason.trim() ? { reason: draft.reason.trim() } : {});
    setBusy(false);
    if (res) { onSaved(res.feature, res.audit); toastStore.getState().show({ message: `Reset ${f.description}` }); }
  };

  return (
    <ListRow as="div" className="members__row admin-features__row" role="row" data-override={live ? "" : undefined}>
      <Cell min={160} className="approvals__org">
        <RowTitle as="b">{f.description}</RowTitle>
        <RowSub>{f.key}{live ? <> · <span className="chip chip--warning">Override</span></> : f.override?.expired ? <> · <span className="chip chip--neutral">Expired</span></> : null}</RowSub>
      </Cell>
      <Cell width={72} className="members__active">{valueText(f.kind, f.default)}</Cell>
      <Cell width={110} title={blocked ? "Private data: offices and the Chicago Politics team only" : undefined}>
        {f.kind === "flag"
          ? <span className={`admin-switch${!canEdit || blocked ? " is-disabled" : ""}`}><Switch on={draft.on} label={f.description} onChange={(on) => canEdit && !blocked && setDraft((d) => ({ ...d, on }))} /></span>
          : <input className="text-input admin-number" type="number" min={0} step={1} inputMode="numeric" value={draft.limit} disabled={!canEdit} aria-label={f.description}
              placeholder="None" aria-invalid={!validLimit || undefined} onChange={(e) => setDraft((d) => ({ ...d, limit: e.target.value }))} />}
      </Cell>
      <Cell width={150}>
        <input className="text-input admin-date" type="date" value={draft.expires} disabled={!canEdit} aria-label={`${f.description}: expires`}
          min={dateInput(new Date(Date.now() + 864e5).toISOString())} onChange={(e) => setDraft((d) => ({ ...d, expires: e.target.value }))} />
      </Cell>
      <Cell min={120}>
        {canEdit
          ? <input className="text-input admin-reason" value={draft.reason} required aria-required aria-label={`${f.description}: reason`} maxLength={500}
              placeholder={live?.reason ?? undefined} onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))} />
          : <span className="members__active admin-audit__reason">{live?.reason ?? ""}</span>}
      </Cell>
      <Cell width={96} align="end" className="approvals__actions">
        {canEdit && <>
          <button className="btn btn--primary btn--small" disabled={busy || !changed || !validLimit || !draft.reason.trim()} onClick={() => void save()}>Save</button>
          {f.override && <button className="btn btn--secondary btn--small" disabled={busy} onClick={() => void reset()} aria-label={`Reset ${f.description} to default`} title="Reset to default"><RotateCcw size={14} aria-hidden /></button>}
        </>}
      </Cell>
    </ListRow>
  );
}

/** "Ward Office + API and embeds", or "None" (the group defaults). */
const planText = (p: PlanSetting | null) => (p?.plan ? [PLANS[p.plan as PlanKey].name, ...p.addons.map((a) => ADDONS[a as AddonKey]?.name ?? a)].join(" + ") : "None");

/** The team's plan and add-ons (staff owners/admins change it, with a reason); the features' defaults follow the plan. */
function PlanPanel({ orgId, canEdit, plans, initial, onSaved }: {
  orgId: string; canEdit: boolean; plans: AdminPlanChoice[]; initial: AdminOrgSnapshot["plan"] | null;
  onSaved: (features: AdminFeature[], entry: AuditEntry | null) => void;
}) {
  const [saved, setSaved] = useState<PlanSetting>({ plan: initial?.key ?? null, addons: initial?.addons ?? [] });
  const [draft, setDraft] = useState<PlanSetting>(saved);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const choice = plans.find((p) => p.key === draft.plan) ?? null;
  const changed = draft.plan !== saved.plan || draft.addons.join(" ") !== saved.addons.join(" ");
  const save = async () => {
    setBusy(true);
    const res = await call<{ plan: AdminOrgSnapshot["plan"]; features: AdminFeature[]; audit: AuditEntry | null }>(
      `/api/dashboard/admin/orgs/${encodeURIComponent(orgId)}/plan`, "PUT", { plan: draft.plan, addons: draft.addons, reason: reason.trim() });
    setBusy(false);
    if (!res) return;
    const next = { plan: res.plan?.key ?? null, addons: res.plan?.addons ?? [] };
    setSaved(next); setDraft(next); setReason("");
    onSaved(res.features, res.audit);
    toastStore.getState().show({ message: `Saved ${planText(next)}` });
  };
  return (
    <section className="approvals-recent" aria-labelledby="admin-plan">
      <h2 className="section-title" id="admin-plan">Plan</h2>
      <List as="div" className="members admin-features" {...table({ "aria-labelledby": "admin-plan" })}>
        <ListRow as="div" className="members__row admin-features__row" role="row">
          <Cell min={160} className="approvals__org">
            <Dropdown value={draft.plan ?? ""} disabled={!canEdit} label="Plan"
              options={[{ value: "", label: "None" }, ...plans.map((p) => ({ value: p.key as string, label: p.name }))]}
              onChange={(v) => setDraft({ plan: (v || null) as PlanSetting["plan"], addons: [] })} />
          </Cell>
          <Cell min={160} className="admin-keys__scopes">
            {(choice?.addons ?? []).map((a) => (
              <label key={a.key} className="chip chip--neutral">
                <input type="checkbox" disabled={!canEdit} checked={draft.addons.includes(a.key)}
                  onChange={(e) => setDraft((d) => ({ ...d, addons: e.target.checked ? [...d.addons, a.key] : d.addons.filter((x) => x !== a.key) }))} />
                {a.name}
              </label>
            ))}
          </Cell>
          <Cell min={120}>
            {canEdit && <input className="text-input admin-reason" value={reason} required aria-required aria-label="Plan: reason" maxLength={500} placeholder="Reason"
              onChange={(e) => setReason(e.target.value)} />}
          </Cell>
          <Cell width={96} align="end" className="approvals__actions">
            {canEdit && <button className="btn btn--primary btn--small" disabled={busy || !changed || !reason.trim()} onClick={() => void save()}>Save</button>}
          </Cell>
        </ListRow>
      </List>
    </section>
  );
}
